'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { REACT_QUERY_CONFIG } from '@vertex-protocol/react-client';
import { WithChildren } from '@vertex-protocol/web-common';
import { ExodusWalletProvider } from 'client/context/ExodusWalletProvider';
import { AnalyticsContextProvider } from 'client/modules/analytics/AnalyticsContextProvider';
import { GoogleAnalytics } from 'client/modules/analytics/GoogleAnalytics';
import { AppDataProviders } from 'client/modules/app/appData/AppDataProviders';
import { StackdPrivyProvider } from 'client/modules/app/appData/StackdPrivyProvider';
import { AppDialogs } from 'client/modules/app/AppDialogs';
import { GatedAppAccessListener } from 'client/modules/app/gatedAppAccess/GatedAppAccessListener';
import { OpenCommandCenterOnKeyPressListener } from 'client/modules/commandCenter/components/OpenCommandCenterOnKeyPressListener';
import { NotificationManagerContextProvider } from 'client/modules/notifications/NotificationManagerContextProvider';
import { SentryConfigManager } from 'client/modules/sentry/SentryConfigManager';
import { OrderFillQueryRefetchListener } from 'client/modules/trading/OrderFillQueryRefetchListener';
import { TpSlPositionChangeListener } from 'client/modules/trading/TpSlPositionChangeListener';
import { OpenWidgetScript } from 'client/modules/helpdesk/OpenWidgetScript';
import { DebugChatWidget } from 'client/modules/debugChat/components/DebugChatWidget';
import { RuntimeContextProvider } from 'client/modules/debugChat/context/RuntimeContextProvider';
import { Provider as JotaiProvider } from 'jotai';
import { Suspense } from 'react';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: REACT_QUERY_CONFIG.defaultQueryStaleTime,
    },
  },
});

export function ClientLayout({ children }: WithChildren) {
  return (
    <StackdPrivyProvider>
      <QueryClientProvider client={queryClient}>
        <JotaiProvider>
          <RuntimeContextProvider>
            <ExodusWalletProvider>
              {/*
            Suspense boundary needed for `AppDataProviders` since it is using `useSearchParams` to avoid forcing
            the whole site into client-side rendering.
            See https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout
            */}
              <Suspense>
                <AppDataProviders>
                  <NotificationManagerContextProvider>
                    <AnalyticsContextProvider>
                      <GoogleAnalytics />
                      {children}
                      <AppDialogs />
                      <GatedAppAccessListener />
                      <OrderFillQueryRefetchListener />
                      <SentryConfigManager />
                      <TpSlPositionChangeListener />
                      <OpenCommandCenterOnKeyPressListener />
                      <OpenWidgetScript />
                    </AnalyticsContextProvider>
                  </NotificationManagerContextProvider>
                </AppDataProviders>
              </Suspense>
            </ExodusWalletProvider>
            <DebugChatWidget />
          </RuntimeContextProvider>
        </JotaiProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </StackdPrivyProvider>
  );
}
